import type { HubSpotProduct, HubSpotField } from "../types/configurator";
import { MOCK_PRODUCTS } from "../data/products";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";
const CACHE_KEY = 'hubspot_products_cache';
const CACHE_TTL = parseInt(import.meta.env.VITE_CACHE_TTL_MS || '600000');

interface CachedProducts {
  data: HubSpotProduct[];
  timestamp: number;
  ttl: number;
}

// Cache functions
function getCachedProducts(): HubSpotProduct[] | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp, ttl }: CachedProducts = JSON.parse(cached);
    if (Date.now() - timestamp > ttl) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedProducts(products: HubSpotProduct[]): void {
  const cached: CachedProducts = {
    data: products,
    timestamp: Date.now(),
    ttl: CACHE_TTL,
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
}

// Main fetch function
export async function fetchProducts(): Promise<HubSpotProduct[]> {
  // 1. Check cache
  const cached = getCachedProducts();
  if (cached) {
    console.log('Using cached products');
    return cached;
  }

  // 2. In dev mode without backend, use mock data
  if (import.meta.env.DEV && !API_BASE.startsWith('http')) {
    console.log('Using mock products (dev mode)');
    await new Promise(r => setTimeout(r, 300));
    return MOCK_PRODUCTS;
  }

  // 3. Fetch from backend proxy
  const res = await fetch(`${API_BASE}/api/hubspot/products`, {
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch products: ${res.status}`);
  }

  const products = await res.json();

  // 4. Cache results
  setCachedProducts(products);

  return products;
}

export function clearProductCache(): boolean {
  const hadCache = localStorage.getItem(CACHE_KEY) !== null;
  localStorage.removeItem(CACHE_KEY);
  return hadCache;
}

export function getCacheMetadata() {
  const cached = localStorage.getItem(CACHE_KEY);
  if (!cached) return null;

  const { timestamp, ttl } = JSON.parse(cached);
  return { lastFetched: timestamp, expiresAt: timestamp + ttl };
}

export async function fetchFields(): Promise<HubSpotField[]> {
  if (import.meta.env.DEV && !API_BASE.startsWith('http')) {
    return [];
  }

  const res = await fetch(`${API_BASE}/api/hubspot/fields`, {
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch fields: ${res.status}`);
  }

  const data = await res.json();
  return data.fields ?? [];
}
